import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { debounce } from 'lodash';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface FeatureToggleFilters {
    search: string;
    showEnabled: boolean;
    showDisabled: boolean;
}

interface FeatureToggleHeaderProps {
    filters: FeatureToggleFilters;
    onFilterChange: (filters: Partial<FeatureToggleFilters>) => void;
    totalToggles: number;
    filteredToggles: number;
}

export const FeatureToggleHeader = ({
    filters,
    onFilterChange,
}: FeatureToggleHeaderProps) => {
    const { t } = useTranslation();
    const [searchInput, setSearchInput] = useState(filters.search);

    const activeFilter = () => {
        if (filters.showEnabled && filters.showDisabled) return 'all';
        if (filters.showEnabled) return 'enabled';
        if (filters.showDisabled) return 'disabled';
        return 'all';
    };

    const debouncedSearch = debounce((value: string) => {
        onFilterChange({ search: value });
    }, 300);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        debouncedSearch(value);
    };

    const clearSearch = () => {
        setSearchInput('');
        debouncedSearch('');
    };

    const showAll = () => {
        onFilterChange({ showEnabled: true, showDisabled: true });
    };

    const showEnabled = () => {
        onFilterChange({ showEnabled: true, showDisabled: false });
    };

    const showDisabled = () => {
        onFilterChange({ showEnabled: false, showDisabled: true });
    };

    return (
        <div className="space-y-4 mb-4">
            <div className="flex flex-col sm:flex-row justify-between gap-4 items-start sm:items-center">
                <div>
                    <h2 className="text-2xl font-bold text-foreground">
                        {t('systemSettings.featureToggles.title')}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('systemSettings.featureToggles.description')}
                    </p>
                </div>

                <div className="flex items-center gap-1 bg-muted p-1 rounded-md">
                    <Button
                        variant={activeFilter() === 'all' ? "default" : "ghost"}
                        size="sm"
                        onClick={showAll}
                        className="h-8 rounded-sm"
                    >
                        {t('systemSettings.featureToggles.filters.all')}
                    </Button>

                    <Button
                        variant={activeFilter() === 'enabled' ? "default" : "ghost"}
                        size="sm"
                        onClick={showEnabled}
                        className="h-8 rounded-sm"
                    >
                        {t('systemSettings.featureToggles.status.enabled')}
                    </Button>

                    <Button
                        variant={activeFilter() === 'disabled' ? "default" : "ghost"}
                        size="sm"
                        onClick={showDisabled}
                        className="h-8 rounded-sm"
                    >
                        {t('systemSettings.featureToggles.status.disabled')}
                    </Button>
                </div>
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    type="search"
                    placeholder={t('systemSettings.featureToggles.search.placeholder')}
                    className="pl-10 pr-10 h-11 rounded-lg transition-all border-muted bg-background focus-visible:bg-background"
                    value={searchInput}
                    onChange={handleSearchChange}
                    aria-label={t('systemSettings.featureToggles.search.ariaLabel')}
                />
                {searchInput && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                        onClick={clearSearch}
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">{t('common.clear')}</span>
                    </Button>
                )}
            </div>
        </div>
    );
}; 